import { Model } from '@/clients/http'

import { configService } from '@/services'
import { adminHeaders } from '@/lib/storefront'
import {
  AssistantCapabilities,
  AssistantConversation,
  AssistantConversationSummary,
  AssistantEvent,
  AssistantLanguage,
  AssistantPageContext,
  AssistantProposal,
  AssistantSetupStatus,
} from '@/types/assistant'

const BASE = '/api/backoffice/copilot'

/** The server's message from a failed request, else `fallback`. */
export const assistantError = (error: unknown, fallback: string) =>
  (error as { data?: { message?: string } } | null)?.data?.message || fallback

export class AssistantModel extends Model {
  public static readonly correlations = {
    CAPABILITIES: 'ASSISTANT_CAPABILITIES',
    CONVERSATIONS: 'ASSISTANT_CONVERSATIONS',
    CONVERSATION: 'ASSISTANT_CONVERSATION',
    DELETE_CONVERSATION: 'ASSISTANT_DELETE_CONVERSATION',
    RATE: 'ASSISTANT_RATE',
    PROPOSAL: 'ASSISTANT_PROPOSAL',
    RESOLVE_PROPOSAL: 'ASSISTANT_RESOLVE_PROPOSAL',
    TRANSCRIBE: 'ASSISTANT_TRANSCRIBE',
    IMAGE: 'ASSISTANT_IMAGE',
    WRITE: 'ASSISTANT_WRITE',
    SETUP: 'ASSISTANT_SETUP',
  }

  public static readonly getCapabilities = async () =>
    this.createHttpRequest<{ result: AssistantCapabilities }>({
      correlationKey: this.correlations.CAPABILITIES,
      payload: { method: 'get', url: `${BASE}/capabilities`, useBearerToken: true },
    })

  public static readonly getSetupStatus = async () =>
    this.createHttpRequest<{ result: AssistantSetupStatus }>({
      correlationKey: this.correlations.SETUP,
      payload: { method: 'get', url: `${BASE}/setup`, useBearerToken: true },
    })

  public static readonly getConversations = async () =>
    this.createHttpRequest<{ result: AssistantConversationSummary[] }>({
      correlationKey: this.correlations.CONVERSATIONS,
      payload: { method: 'get', url: `${BASE}/conversations`, useBearerToken: true },
    })

  public static readonly getConversation = async (conversationUid: string) =>
    this.createHttpRequest<{ result: AssistantConversation }>({
      correlationKey: this.correlations.CONVERSATION,
      payload: { method: 'get', url: `${BASE}/conversations/${conversationUid}`, useBearerToken: true },
    })

  public static readonly deleteConversation = async (conversationUid: string) =>
    this.createHttpRequest<null>({
      correlationKey: this.correlations.DELETE_CONVERSATION,
      payload: { method: 'delete', url: `${BASE}/conversations/${conversationUid}`, useBearerToken: true },
    })

  public static readonly rateMessage = async (conversationUid: string, messageId: number, rating: 1 | -1) =>
    this.createHttpRequest<null>({
      correlationKey: this.correlations.RATE,
      payload: { method: 'put', url: `${BASE}/conversations/${conversationUid}/messages/${messageId}/rating`, body: { rating }, useBearerToken: true },
    })

  public static readonly getProposal = async (proposalUid: string) =>
    this.createHttpRequest<{ result: AssistantProposal }>({
      correlationKey: this.correlations.PROPOSAL,
      payload: { method: 'get', url: `${BASE}/proposals/${proposalUid}`, useBearerToken: true },
    })

  public static readonly resolveProposal = async (proposalUid: string, status: 'published' | 'discarded') =>
    this.createHttpRequest<{ result: { status: string } }>({
      correlationKey: this.correlations.RESOLVE_PROPOSAL,
      payload: { method: 'put', url: `${BASE}/proposals/${proposalUid}`, body: { status }, useBearerToken: true },
    })

  public static readonly transcribe = async (audio: Blob, language: AssistantLanguage) => {
    const body = new FormData()
    body.append('file', audio, `recording.${audio.type.includes('wav') ? 'wav' : audio.type.includes('ogg') ? 'ogg' : audio.type.includes('mp4') ? 'm4a' : 'webm'}`)
    body.append('language', language)
    return this.createHttpRequest<{ result: { text: string } }>({
      correlationKey: this.correlations.TRANSCRIBE,
      payload: { method: 'post', url: `${BASE}/transcribe`, body, multipart: true, useBearerToken: true },
    })
  }

  public static readonly generateImage = async (prompt: string, language: AssistantLanguage, reference?: File) => {
    const body = new FormData()
    body.append('prompt', prompt)
    body.append('language', language)
    if (reference) body.append('file', reference)
    return this.createHttpRequest<{ result: { media_id: string; path: string; name: string } }>({
      correlationKey: this.correlations.IMAGE,
      payload: { method: 'post', url: `${BASE}/images`, body, multipart: true, useBearerToken: true },
    })
  }

  public static readonly writeProductText = async (body: { field: 'description' | 'summary'; title: string; notes?: string; current?: string; language: AssistantLanguage }) =>
    this.createHttpRequest<{ result: { text: string } }>({
      correlationKey: this.correlations.WRITE,
      payload: { method: 'post', url: `${BASE}/write`, body, useBearerToken: true },
    })
}

export interface StreamMessageBody {
  conversation_uid?: string
  message: string
  language: AssistantLanguage
  source: 'message' | 'voice'
  page?: AssistantPageContext
}

/**
 * Sends one message and calls `onEvent` for each Server-Sent Event of the reply. POST with fetch (EventSource is
 * GET-only); the same headers as the admin's API client.
 */
export const streamAssistantMessage = async (body: StreamMessageBody, onEvent: (event: AssistantEvent) => void, signal: AbortSignal) => {
  const baseUrl = configService.getConfig()?.apiConfig?.baseUrl ?? ''
  const res = await fetch(`${baseUrl}${BASE}/messages`, {
    method: 'POST',
    headers: { ...adminHeaders(), Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok || !res.body) {
    const json = (await res.json().catch(() => null)) as { message?: string; errors?: { message: string }[] } | null
    throw new Error(json?.errors?.[0]?.message || json?.message || `The assistant is unavailable (${res.status}).`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      const chunk = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const data = chunk
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n')
      if (data) {
        try {
          onEvent(JSON.parse(data) as AssistantEvent)
        } catch (error) {
          console.error('Assistant: unreadable event', error)
        }
      }
      boundary = buffer.indexOf('\n\n')
    }
  }
}
